/**
 * /design — the disruptive single-stage design surface.
 *
 * Beats the grid-of-room-cards layout every competitor ships. Pattern:
 *   ┌────────────────────────────────────────────────────┐
 *   │ Thumbnail strip — every room with score badge      │
 *   ├──────────────────────────────────┬─────────────────┤
 *   │ Stage — selected room, full plan │ Agent rail —    │
 *   │ + SKUs on the plan + scores      │ live suggestions│
 *   │                                  │ + Why traces    │
 *   └──────────────────────────────────┴─────────────────┘
 *
 * Every value is derived from the live project graph + active country.
 * No hardcoded copy. No stub. The agent walks promoted rules AND the
 * room-anchor heuristics; both feed the same typed Suggestion bus.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Ruler, Tag, ArrowRight, Sparkles, Layers, Box,
  CircleCheck, AlertTriangle, IndianRupee, Lightbulb, X as XIcon, Undo2, Redo2,
  Sun, Moon, Sunset, ScanLine, Wand2,
} from "lucide-react";
import { SuggestionStrip } from "../components/rules/builder/SuggestionStrip";
import { Scene3D } from "../components/design/Scene3D";
import { CatalogRail } from "../components/design/CatalogRail";
import { ScanRoomCard } from "../components/design/ScanRoomCard";
import { ComplianceAuditBar } from "../components/design/ComplianceAuditBar";
import { TimelinePanel } from "../components/design/TimelinePanel";
import { ContextualChat } from "../components/tracker/ContextualChat";
import { recordEvent, resolveActor } from "../lib/agent/timeline";
import { submitRender, composeFluxPrompt } from "../lib/comfy/client";
import { FLUX_STYLES, FLUX_ROOM_CUES, FLUX_SIZES } from "../data/generated/flux-prompts.generated";
import { createAgentBus, type AgentBus } from "../lib/agent/bus";
import { orchestrate } from "../lib/agent/orchestrator";
import { addPlacement, removePlacementByPair, isUserAdded, undoPlacement, redoPlacement, undoDepth, redoDepth, setPlacementPosition, rotatePlacement } from "../lib/userPlacements";
import { mergeProjectLayers } from "../lib/projectLayers";
import { useActiveProjectGraph } from "../lib/useActiveProjectGraph";
import { renderFloorPlanSvg, getPlanMetrics } from "../lib/floorplanSvg";
import { FloorPlanOverlay } from "../components/design/FloorPlanOverlay";
import { FloorPlan2DTldraw } from "../components/design/FloorPlan2DTldraw";
import { wouldFit } from "../lib/skuFitCheck";
import { env } from "../lib/env";
import { MoodBoard } from "../components/inspiration/MoodBoard";
import { formatCompactMoney, formatMoney } from "../lib/i18n/format";
import { photoForSkuNode } from "../data/imagery";
import { listPromoted } from "../lib/projectRules";
import { DESIGNER_RULES } from "../data/designerRules";
import { dryRun } from "../lib/ruleEvaluate";
import type { GraphNode, ProjectGraph } from "../types/ontology";

interface RoomScore {
  style: number;       // 0..1 cohesion (avg styleMatch across SKUs in room)
  budgetUtil: number;  // 0..1, BudgetLine sum vs cap share
  leadRiskMax: number; // max leadDays among SKUs (days)
  placements: number;
  /** Locally-evaluable rule violations from listPromoted × dryRun. */
  rulesFired: number;
}

function attrNum(n: GraphNode | undefined, key: string): number | null {
  if (!n) return null;
  const v = n.attrs[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}

function attrStr(n: GraphNode | undefined, key: string): string | null {
  if (!n) return null;
  const v = n.attrs[key];
  return typeof v === "string" ? v : null;
}

function skusInRoom(graph: ProjectGraph, roomId: string): GraphNode[] {
  const ids = new Set(
    graph.edges
      .filter((e) => e.kind === "IN_ROOM" && e.target === roomId)
      .map((e) => e.source),
  );
  return graph.nodes.filter((n) => n.kind === "SKU" && ids.has(n.id));
}

function budgetForRoom(graph: ProjectGraph, roomId: string): number {
  const lines = graph.edges
    .filter((e) => e.kind === "FOR_ROOM" && e.target === roomId)
    .map((e) => graph.nodes.find((n) => n.id === e.source && n.kind === "BudgetLine"))
    .filter((x): x is GraphNode => Boolean(x));
  return lines.reduce((s, l) => s + (attrNum(l, "amount") ?? 0), 0);
}

function scoreRoom(graph: ProjectGraph, room: GraphNode, rulesFired: number): RoomScore {
  const skus = skusInRoom(graph, room.id);
  const styleMatches = skus.map((s) => attrNum(s, "styleMatch") ?? 0).filter((x) => x > 0);
  const style = styleMatches.length > 0 ? styleMatches.reduce((a, b) => a + b, 0) / styleMatches.length : 0;
  const leadRiskMax = skus.reduce((m, s) => Math.max(m, attrNum(s, "leadDays") ?? 0), 0);
  const budgetUsed = budgetForRoom(graph, room.id);
  const project = graph.nodes.find((n) => n.kind === "Project");
  const cap = attrNum(project, "budgetCap") ?? 0;
  const budgetUtil = cap > 0 ? Math.min(1, budgetUsed / cap) : 0;
  return { style, budgetUtil, leadRiskMax, placements: skus.length, rulesFired };
}

function diagnoseRoom(room: GraphNode, skus: ReadonlyArray<GraphNode>): { reason: string; missing: string }[] {
  const kind = (attrStr(room, "roomKind") ?? "").toLowerCase();
  const labels = skus.map((s) => s.label.toLowerCase());
  const has = (needle: string): boolean => labels.some((l) => l.includes(needle));
  const out: { reason: string; missing: string }[] = [];
  if (kind.includes("kitchen") && !has("island") && !has("countertop"))
    out.push({ reason: "validator/kitchen-missing-anchor", missing: "kitchen island or countertop" });
  if (kind.includes("dining") && !has("dining") && !has("table"))
    out.push({ reason: "validator/dining-missing-table", missing: "dining table" });
  if (kind.includes("living") && !has("sofa") && !has("seat"))
    out.push({ reason: "validator/living-missing-seating", missing: "sofa" });
  if (kind.includes("bed") && !has("bed") && !has("wardrobe"))
    out.push({ reason: "validator/bedroom-missing-anchor", missing: "bed or wardrobe" });
  return out;
}

type View = "2d" | "3d" | "moodboard";

export default function Design(): JSX.Element {
  const { graph: baseGraph, activeId } = useActiveProjectGraph();
  // User-added placements live in localStorage and merge into the graph here.
  // A tick state forces re-merge when CatalogRail adds a new SKU.
  const [tick, setTick] = useState(0);
  const graph = useMemo(
    // Merge every typed layer (user placements + imported SKUs +
    // CatalogImport activities + scheduleSynth) onto the base graph
    // via the shared helper so Project map / Design / Tracker see
    // the same full picture. tick forces re-merge on catalog clicks.
    () => { void tick; return mergeProjectLayers(baseGraph, activeId); },
    [baseGraph, activeId, tick],
  );

  const [bus, setBus] = useState<AgentBus>(() => createAgentBus());
  useEffect(() => { setBus(createAgentBus()); }, [activeId]);

  const [view, setView] = useState<View>("2d");
  const [focus3d, setFocus3d] = useState<boolean>(false);
  const [lighting3d, setLighting3d] = useState<"day" | "evening" | "night">("day");
  const [camera3d, setCamera3d] = useState<"perspective" | "topdown">("perspective");
  const [measureMode, setMeasureMode] = useState<boolean>(false);
  const [labelsOn, setLabelsOn] = useState<boolean>(true);
  const [catalogCollapsed, setCatalogCollapsed] = useState<boolean>(false);
  // Transient rejection toast — shown when the agent blocks a catalog
  // click so the user knows WHY the add didn't land.
  const [rejectionToast, setRejectionToast] = useState<{ message: string; citation: string } | null>(null);
  // Render submit state — when the user clicks Flux we surface progress
  // here (status + friendly error from explainComfyError).
  const [renderState, setRenderState] = useState<{ status: "idle" | "submitting" | "ok" | "error"; message: string }>({ status: "idle", message: "" });
  // Once the ComfyUI tunnel returns 5xx, treat it as known-unbound for
  // the rest of the session — disable the Flux button so the user
  // can't keep triggering the same upstream failure. Re-enables on
  // page reload (when the user wants to retry after the tunnel comes
  // back). Mirrors the connection-modal backendUnbound pattern.
  const [renderUnbound, setRenderUnbound] = useState<boolean>(false);
  const rooms = useMemo(() => graph.nodes.filter((n) => n.kind === "Room"), [graph.nodes]);

  // Undo/redo keyboard shortcuts (⌘Z / ⌘⇧Z) — designer's standard. The tick
  // bump re-merges user placements into the graph immediately.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      const tag = (e.target as HTMLElement | null)?.tagName ?? "";
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement | null)?.isContentEditable) return;
      if (e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        if (undoPlacement(activeId)) setTick((t) => t + 1);
      } else if ((e.key === "z" && e.shiftKey) || e.key === "y") {
        e.preventDefault();
        if (redoPlacement(activeId)) setTick((t) => t + 1);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeId]);

  const undoN = undoDepth(activeId);
  const redoN = redoDepth(activeId);
  const [selectedId, setSelectedId] = useState<string | null>(rooms[0]?.id ?? null);
  useEffect(() => {
    if (selectedId === null || !rooms.some((r) => r.id === selectedId)) {
      setSelectedId(rooms[0]?.id ?? null);
    }
  }, [rooms, selectedId]);

  // Walk PROMOTED RULES against the live graph nodes per the agent contract.
  const promotedIds = useMemo(() => listPromoted(activeId), [activeId]);
  const promotedRules = useMemo(() => {
    const byId = new Map(DESIGNER_RULES.map((r) => [r.id, r]));
    return promotedIds.map((id) => byId.get(id)).filter((r): r is typeof DESIGNER_RULES[number] => Boolean(r));
  }, [promotedIds]);

  // Aggregate score map (rule-fires + diagnose) per room.
  const scores = useMemo<ReadonlyMap<string, RoomScore>>(() => {
    const m = new Map<string, RoomScore>();
    // Count rule fires per room — a fire that matches a Room node counts there;
    // a fire matching an SKU counts toward its room via IN_ROOM.
    const fireByRoom = new Map<string, number>();
    for (const rule of promotedRules) {
      const dr = dryRun(rule.predicate, graph.nodes);
      for (const id of dr.matched) {
        const node = graph.nodes.find((n) => n.id === id);
        if (!node) continue;
        if (node.kind === "Room") {
          fireByRoom.set(id, (fireByRoom.get(id) ?? 0) + 1);
        } else if (node.kind === "SKU") {
          const rEdge = graph.edges.find((e) => e.kind === "IN_ROOM" && e.source === id);
          if (rEdge) fireByRoom.set(rEdge.target, (fireByRoom.get(rEdge.target) ?? 0) + 1);
        }
      }
    }
    for (const room of rooms) {
      m.set(room.id, scoreRoom(graph, room, fireByRoom.get(room.id) ?? 0));
    }
    return m;
  }, [graph, rooms, promotedRules]);

  // Emit Suggestions: missing-anchor heuristics + each promoted-rule fire.
  useEffect(() => {
    const now = new Date().toISOString();
    for (const room of rooms) {
      const skus = skusInRoom(graph, room.id);
      for (const i of diagnoseRoom(room, skus)) {
        bus.emit({
          agentId: "design-room-agent",
          confidence: 0.86,
          about: { kind: "Room", id: room.id },
          wasInformedBy: [i.reason],
          proposed: {
            kind: "create", nodeKind: "Placement",
            subjectId: `proposed-${room.id}-${i.missing.replace(/\s/g, "-")}`,
            attrs: { suggestion: "add-anchor-sku", roomLabel: room.label, missing: i.missing },
            ts: now, origin: "agent",
          },
        });
      }
    }
    for (const rule of promotedRules) {
      const dr = dryRun(rule.predicate, graph.nodes);
      for (const id of dr.matched.slice(0, 5)) {
        const node = graph.nodes.find((n) => n.id === id);
        if (!node) continue;
        bus.emit({
          agentId: "design-rule-agent",
          confidence: 0.91,
          about: { kind: node.kind, id },
          wasInformedBy: [`rule/${rule.id}`],
          proposed: {
            kind: "update", nodeKind: node.kind, subjectId: id,
            attrs: { ruleId: rule.id, ruleTitle: rule.title, severity: rule.severity, fix: rule.because },
            ts: now, origin: "agent",
          },
        });
      }
    }
    // Research-backed agentic suggestions — style cohesion, budget pull,
    // lead-day risk, NKBA work-triangle, Vastu mismatch, missing Approval.
    for (const proposal of orchestrate(graph, rooms)) {
      bus.emit(proposal);
    }
  }, [bus, rooms, graph, promotedRules]);

  // Live compliance proposals — used by the ComplianceAuditBar above the
  // stage. Re-derived per render so deletions reflect immediately.
  const complianceProposals = useMemo(() => orchestrate(graph, rooms), [graph, rooms]);

  const selectedRoom = rooms.find((r) => r.id === selectedId) ?? null;

  // SKU count for the collapsed catalog drawer header.
  const catalogSkuCount = useMemo(() => graph.nodes.filter((n) => n.kind === "SKU").length, [graph.nodes]);

  return (
    <>
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 pt-4 pb-[260px] space-y-2">
      <header className="rounded-2xl border border-border/70 bg-gradient-to-br from-foyr/10 via-cream to-sea/5 px-4 py-2.5 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow inline-flex items-center gap-1.5">
              <Ruler className="w-3 h-3" /> Design · {graph.projectName}
            </div>
            <h1 className="font-newyork text-[22px] mt-0.5 text-ink-950 leading-tight tracking-tight">
              {rooms.length} rooms. {sumPlacements(graph, rooms)} placements. One brain.
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] tabular">
            <button
              type="button"
              disabled={undoN === 0}
              onClick={() => { if (undoPlacement(activeId)) setTick((t) => t + 1); }}
              className={"inline-flex items-center gap-1 px-2 py-1 rounded-lg border transition " + (undoN === 0 ? "border-border text-ink-500 opacity-50 cursor-not-allowed" : "border-border text-ink-700 hover:border-foyr/40 hover:text-ink-950")}
              title="Undo last placement (⌘Z)"
            >
              <Undo2 className="w-3 h-3" /> Undo{undoN > 0 ? ` (${undoN})` : ""}
            </button>
            <button
              type="button"
              disabled={redoN === 0}
              onClick={() => { if (redoPlacement(activeId)) setTick((t) => t + 1); }}
              className={"inline-flex items-center gap-1 px-2 py-1 rounded-lg border transition " + (redoN === 0 ? "border-border text-ink-500 opacity-50 cursor-not-allowed" : "border-border text-ink-700 hover:border-foyr/40 hover:text-ink-950")}
              title="Redo last undone (⌘⇧Z)"
            >
              <Redo2 className="w-3 h-3" /> Redo{redoN > 0 ? ` (${redoN})` : ""}
            </button>
            <ViewTab label="2D plan" icon={<Layers className="w-3 h-3" />} active={view === "2d"} onClick={() => setView("2d")} />
            <ViewTab label="3D" icon={<Box className="w-3 h-3" />} active={view === "3d"} onClick={() => setView("3d")} />
            <ViewTab label="Moodboard" icon={<Sparkles className="w-3 h-3" />} active={view === "moodboard"} onClick={() => setView("moodboard")} />
          </div>
        </div>
      </header>

      {/* Thumbnail strip — compact single-row room picker. */}
      {rooms.length > 0 ? (
        <nav className="flex gap-1 overflow-x-auto -mx-1 px-1" role="tablist" aria-label="Rooms">
          {rooms.map((r) => {
            const s = scores.get(r.id);
            const active = r.id === selectedId;
            return (
              <button
                key={r.id}
                role="tab"
                aria-selected={active}
                onClick={() => setSelectedId(r.id)}
                className={
                  "shrink-0 inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-1 transition text-[11px] " +
                  (active
                    ? "bg-ink-950 border-ink-950 text-cream"
                    : "bg-cream border-border hover:border-foyr/40 hover:bg-cream-dark/50 text-ink-700")
                }
                title={`${r.label}${attrNum(r, "area") !== null ? ` · ${attrNum(r, "area")} sq ft` : ""} · ${s?.placements ?? 0} SKUs`}
              >
                <span className="font-medium truncate max-w-[140px]">{r.label}</span>
                <span className={"tabular " + (active ? "text-cream/70" : "text-ink-500")}>
                  {s?.placements ?? 0}
                </span>
                {(s?.rulesFired ?? 0) > 0 ? (
                  <span className={"pill !py-0 !px-1 text-[9px] border " + (active ? "border-foyr/40 bg-foyr/15 text-foyr" : "border-foyr/40 bg-foyr/10 text-foyr-deep")}>
                    {s?.rulesFired}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>
      ) : null}

      {/* Compliance Audit bar — the moat. Walks NBC 2016, NKBA, ANSI A117.1,
          RERA, IGBC, ECBC, GRIHA, Vastu detectors over the project graph,
          scores by statutory framework, shows the Why-chain. Foyr Neo
          cannot ship this — they have no typed rule layer. */}
      <ComplianceAuditBar
        graph={graph}
        proposals={complianceProposals}
        onJumpToRoom={(roomId) => setSelectedId(roomId)}
      />

      {/* Persistent "Working on: <Room>" workspace card — keeps the active
          room's context anchored regardless of 2D/3D view. SKUs placed in
          this room with one-click remove; area utilization; live rule fires. */}
      {selectedRoom !== null ? (
        <RoomWorkspaceCard
          graph={graph}
          room={selectedRoom}
          score={scores.get(selectedRoom.id)}
          onRemove={(skuId, roomId) => {
            if (removePlacementByPair(activeId, skuId, roomId)) setTick((t) => t + 1);
          }}
          activeProjectId={activeId}
        />
      ) : null}

      {/* Stage + agent rail */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-4 items-start">
        {view === "moodboard" ? (
          <section className="panel p-3 space-y-2 relative">
            <div className="flex items-center justify-between gap-2 px-1 mb-1">
              <div className="text-[11px] uppercase tracking-wider text-ink-500 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Moodboard · inspiration drives styleMatch + the agentic palette
              </div>
              <span className="text-[10.5px] text-ink-500 tabular">{graph.projectName}</span>
            </div>
            <MoodBoard />
          </section>
        ) : view === "3d" ? (
          <section className="panel p-3 space-y-2 relative">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] uppercase tracking-wider text-ink-500 flex items-center gap-1.5">
                <Box className="w-3 h-3" /> 3D scene · drag to orbit · scroll to zoom · {focus3d ? `focused on ${selectedRoom?.label ?? "room"}` : "selected room highlighted in orange"}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setFocus3d((f) => !f)}
                  disabled={selectedId === null}
                  className={
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] transition " +
                    (focus3d
                      ? "bg-ink-950 text-cream border-ink-950"
                      : "bg-cream border-border text-ink-700 hover:border-foyr/40 hover:text-ink-950")
                    + (selectedId === null ? " opacity-50 cursor-not-allowed" : "")
                  }
                  title={focus3d ? "Show all rooms" : "Focus on selected room only"}
                >
                  {focus3d ? "Show all" : "Focus this room"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const node = document.getElementById("scene3d-host");
                    if (!node) return;
                    if (document.fullscreenElement === node) {
                      document.exitFullscreen().catch((err) => { console.error("[Design] exit fullscreen failed", err); });
                    } else {
                      node.requestFullscreen().catch((err) => { console.error("[Design] enter fullscreen failed", err); });
                    }
                  }}
                  className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5"
                  title="Expand to full-screen (Esc to exit)"
                >
                  <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" strokeLinecap="round" />
                  </svg>
                  Expand
                </button>
              </div>
            </div>
            <div id="scene3d-host" className="bg-cream rounded-xl relative">
              <Scene3D
                graph={graph}
                selectedRoomId={selectedId}
                focusOnly={focus3d}
                lighting={lighting3d}
                camera={camera3d}
                measureMode={measureMode}
                showLabels={labelsOn}
                onSkuMoved={(skuId, roomId, xRel, zRel) => {
                  // Persist + re-merge so the next render picks up the override.
                  setPlacementPosition(activeId, skuId, roomId, xRel, zRel);
                  setTick((t) => t + 1);
                  const sku = graph.nodes.find((n) => n.id === skuId);
                  const room = graph.nodes.find((n) => n.id === roomId);
                  if (sku && room) {
                    const userId = (typeof window !== "undefined" && window.localStorage.getItem("foyrmind.userEmail")) || "designer";
                    recordEvent(activeId, {
                      actorKind: "human", actorId: userId,
                      verb: "added", subjectKind: "SKU", subjectId: skuId, subjectLabel: sku.label,
                      verdict: "info",
                      summary: `Moved ${sku.label} in ${room.label} to (${xRel.toFixed(2)}m, ${zRel.toFixed(2)}m).`,
                      citation: "user/scene3d-drag",
                    });
                  }
                }}
                onSkuRotated={(skuId, roomId, nextYaw) => {
                  // Persist absolute yaw; rotatePlacement normalises and writes.
                  // Re-merge so Scene3D picks up the new posYaw_${roomId} attr.
                  const sku = graph.nodes.find((n) => n.id === skuId);
                  const room = graph.nodes.find((n) => n.id === roomId);
                  // Compute the delta from current yaw on the graph, apply via
                  // rotatePlacement (which handles persistence).
                  const currentYaw = sku ? Number(sku.attrs[`posYaw_${roomId}`] ?? 0) : 0;
                  rotatePlacement(activeId, skuId, roomId, nextYaw - currentYaw);
                  setTick((t) => t + 1);
                  if (sku && room) {
                    const userId = (typeof window !== "undefined" && window.localStorage.getItem("foyrmind.userEmail")) || "designer";
                    const degrees = Math.round((nextYaw * 180) / Math.PI);
                    recordEvent(activeId, {
                      actorKind: "human", actorId: userId,
                      verb: "added", subjectKind: "SKU", subjectId: skuId, subjectLabel: sku.label,
                      verdict: "info",
                      summary: `Rotated ${sku.label} in ${room.label} to ${degrees}°.`,
                      citation: "user/scene3d-rotate",
                    });
                  }
                }}
              />
              <StageToolRail
                lighting={lighting3d}
                onLighting={setLighting3d}
                camera={camera3d}
                onCamera={setCamera3d}
                measureMode={measureMode}
                onMeasure={() => setMeasureMode((m) => !m)}
                labelsOn={labelsOn}
                onToggleLabels={() => setLabelsOn((v) => !v)}
                onFlux={async () => {
                  if (renderUnbound) return; // sticky-disabled this session
                  setRenderState({ status: "submitting", message: "Composing prompt + queueing render..." });
                  try {
                    const style = FLUX_STYLES[0]!;
                    const roomCue = FLUX_ROOM_CUES.find((c) => c.kind === "living") ?? null;
                    const size = FLUX_SIZES[0]!;
                    const prompt = composeFluxPrompt({ style, roomCue, size });
                    const ticket = await submitRender({ style, roomCue, size, customPrompt: prompt.positive });
                    setRenderState({ status: "ok", message: `Queued render ${ticket.prompt_id.slice(0, 8)} - polling...` });
                  } catch (err) {
                    const msg = err instanceof Error ? err.message : "Render failed.";
                    setRenderState({ status: "error", message: msg });
                    // Tunnel-down signals: HTML error page / 5xx / unreachable.
                    if (/HTTP 5|unreachable|tunnel|unavailable|unbound/i.test(msg)) {
                      setRenderUnbound(true);
                    }
                  }
                }}
                renderUnbound={renderUnbound}
                renderStatus={renderState.status}
              />
              {rejectionToast !== null ? (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-4 max-w-[480px] z-20 pointer-events-auto">
                  <div className="rounded-2xl border border-foyr-deep/40 bg-foyr-deep text-cream px-3.5 py-2.5 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.45)] flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium leading-snug">Agent rejected the placement</div>
                      <div className="text-[11.5px] text-cream/85 leading-snug mt-0.5">{rejectionToast.message}</div>
                      <div className="text-[10px] text-cream/65 tabular mt-1">{rejectionToast.citation}</div>
                    </div>
                    <button type="button" onClick={() => setRejectionToast(null)} className="text-cream/70 hover:text-cream text-[12px] leading-none mt-0.5">×</button>
                  </div>
                </div>
              ) : null}
              {renderState.status !== "idle" ? (
                <div className="absolute left-3 bottom-3 max-w-[440px] z-20 pointer-events-auto">
                  <div
                    className={
                      "rounded-2xl border px-3.5 py-2.5 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.35)] flex items-start gap-2 " +
                      (renderState.status === "error"
                        ? "bg-foyr-deep text-cream border-foyr-deep/40"
                        : renderState.status === "ok"
                          ? "bg-fern text-cream border-fern/40"
                          : "bg-ink-950 text-cream border-ink-950")
                    }
                  >
                    <Wand2 className="w-4 h-4 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium leading-snug">
                        {renderState.status === "submitting" ? "Submitting to ComfyUI" :
                         renderState.status === "ok"         ? "Render queued" :
                                                                "Render failed"}
                      </div>
                      <div className="text-[11.5px] leading-snug mt-0.5 opacity-90">{renderState.message}</div>
                    </div>
                    <button type="button" onClick={() => setRenderState({ status: "idle", message: "" })} className="opacity-70 hover:opacity-100 text-[12px] leading-none mt-0.5">×</button>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        ) : selectedRoom !== null ? (
          <RoomStage
            graph={graph}
            room={selectedRoom}
            score={scores.get(selectedRoom.id)}
            onRemove={(skuId, roomId) => {
              if (removePlacementByPair(activeId, skuId, roomId)) setTick((t) => t + 1);
            }}
            activeProjectId={activeId}
            onSkuMoved={(skuId, roomId, xRel, zRel) => {
              setPlacementPosition(activeId, skuId, roomId, xRel, zRel);
              setTick((t) => t + 1);
              const sku = graph.nodes.find((n) => n.id === skuId);
              const room = graph.nodes.find((n) => n.id === roomId);
              if (sku && room) {
                const userId = (typeof window !== "undefined" && window.localStorage.getItem("foyrmind.userEmail")) || "designer";
                recordEvent(activeId, {
                  actorKind: "human", actorId: userId,
                  verb: "added", subjectKind: "SKU", subjectId: skuId, subjectLabel: sku.label,
                  verdict: "info",
                  summary: `Moved ${sku.label} in ${room.label} to (${xRel.toFixed(2)}m, ${zRel.toFixed(2)}m) via 2D plan.`,
                  citation: "user/floorplan-drag",
                });
              }
            }}
            onSkuRotated={(skuId, roomId, nextYaw) => {
              const sku = graph.nodes.find((n) => n.id === skuId);
              const currentYaw = sku ? Number(sku.attrs[`posYaw_${roomId}`] ?? 0) : 0;
              rotatePlacement(activeId, skuId, roomId, nextYaw - currentYaw);
              setTick((t) => t + 1);
              const room = graph.nodes.find((n) => n.id === roomId);
              if (sku && room) {
                const userId = (typeof window !== "undefined" && window.localStorage.getItem("foyrmind.userEmail")) || "designer";
                const degrees = Math.round((nextYaw * 180) / Math.PI);
                recordEvent(activeId, {
                  actorKind: "human", actorId: userId,
                  verb: "added", subjectKind: "SKU", subjectId: skuId, subjectLabel: sku.label,
                  verdict: "info",
                  summary: `Rotated ${sku.label} in ${room.label} to ${degrees}° via 2D plan.`,
                  citation: "user/floorplan-rotate",
                });
              }
            }}
            onCatalogDrop={(skuId, roomId, xRelM, zRelM) => {
              // Catalog → plan drop. Same accept/reject path as the
              // catalog-rail click: typed FitVerdict → addPlacement →
              // setPlacementPosition (so the SKU lands where the cursor
              // released) → emit Suggestion + record TimelineEvent.
              const sku = graph.nodes.find((n) => n.id === skuId && n.kind === "SKU");
              const room = graph.nodes.find((n) => n.id === roomId && n.kind === "Room");
              if (!sku || !room) return;
              const verdict = wouldFit(graph, room, sku);
              const roomLabel = room.label;
              if (!verdict.ok) {
                bus.emit({
                  agentId: "design-fit-check",
                  confidence: 1.0,
                  about: { kind: "Room", id: roomId },
                  wasInformedBy: [verdict.citation, "user/catalog-plan-dropped-rejected"],
                  proposed: {
                    kind: "create", nodeKind: "Suggestion",
                    subjectId: `reject-drop-${skuId}-${roomId}-${Date.now()}`,
                    attrs: {
                      ruleId: "R-agent-rejects-placement",
                      ruleTitle: `Agent rejects drop: ${sku.label} in ${roomLabel}`,
                      severity: "block",
                      fix: verdict.reason,
                      citation: verdict.citation,
                      rejectedSkuId: skuId,
                      rejectedSkuLabel: sku.label,
                      targetRoomId: roomId,
                    },
                    ts: new Date().toISOString(), origin: "agent",
                  },
                });
                recordEvent(activeId, {
                  actorKind: "agent", actorId: "spatial-fit-check",
                  verb: "rejected", subjectKind: "SKU", subjectId: skuId, subjectLabel: sku.label,
                  verdict: "block",
                  summary: `Cannot drop ${sku.label} in ${roomLabel}.`,
                  citation: verdict.citation, fix: verdict.reason,
                });
                setRejectionToast({ message: `${resolveActor("agent", "spatial-fit-check").displayName}: ${verdict.reason}`, citation: verdict.citation });
                window.setTimeout(() => setRejectionToast(null), 6000);
                return;
              }
              addPlacement(activeId, skuId, roomId);
              setPlacementPosition(activeId, skuId, roomId, xRelM, zRelM);
              setTick((t) => t + 1);
              const isUnusual = verdict.soft !== undefined;
              bus.emit({
                agentId: "design-user-add",
                confidence: 1.0,
                about: { kind: "SKU", id: skuId },
                wasInformedBy: ["user/catalog-plan-dropped", verdict.citation],
                proposed: {
                  kind: "create", nodeKind: "Placement",
                  subjectId: `user-drop-${skuId}-${roomId}`,
                  attrs: {
                    ruleId: isUnusual ? "R-unusual-placement" : "R-user-added-placement",
                    ruleTitle: isUnusual
                      ? `Unusual placement allowed: ${sku.label} in ${roomLabel}`
                      : "Dropped from catalog",
                    severity: isUnusual ? "warn" : "info",
                    fix: isUnusual
                      ? (verdict.soft ?? verdict.reason)
                      : `${sku.label} dropped at (${xRelM.toFixed(2)} m, ${zRelM.toFixed(2)} m) in ${roomLabel}. ${verdict.reason}`,
                    citation: verdict.citation,
                  },
                  ts: new Date().toISOString(), origin: "user",
                },
              });
              const userId = (typeof window !== "undefined" && window.localStorage.getItem("foyrmind.userEmail")) || "designer";
              recordEvent(activeId, {
                actorKind: "human", actorId: userId,
                verb: "added", subjectKind: "SKU", subjectId: skuId, subjectLabel: sku.label,
                verdict: isUnusual ? "warn" : "info",
                summary: `${sku.label} dropped at (${xRelM.toFixed(2)} m, ${zRelM.toFixed(2)} m) in ${roomLabel}.`,
                citation: verdict.citation,
                ...(isUnusual && verdict.soft !== undefined ? { fix: verdict.soft } : {}),
              });
            }}
          />
        ) : (
          <div className="panel p-8 text-center text-ink-500 text-[13px]">This project has no Room nodes yet.</div>
        )}
        <div className="space-y-3">
          <ContextualChat surfaceId="design" defaultAgentId="spatial-fit-check" />
          <AgentRail bus={bus} />
          <TimelinePanel />
        </div>
      </div>

    </div>

    {/* Sticky catalog drawer — pinned to the viewport bottom so "add" is
        always one click away regardless of how far the page has scrolled.
        Collapsible to a thin handle so the stage stays unobstructed when
        the designer wants a clean view. */}
    <div className="fixed left-0 right-0 bottom-0 z-20 pointer-events-none">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 pb-3">
        <div className="pointer-events-auto rounded-2xl border border-border/70 bg-cream/95 backdrop-blur-md shadow-[0_-4px_18px_-8px_rgba(0,0,0,0.18)]">
          <button
            type="button"
            onClick={() => setCatalogCollapsed((c) => !c)}
            aria-expanded={!catalogCollapsed}
            className="w-full flex items-center justify-between px-4 py-2 text-[11px] uppercase tracking-[0.14em] text-ink-500 hover:text-ink-950 transition"
          >
            <span className="inline-flex items-center gap-2">
              <Tag className="w-3 h-3" /> Catalog · {catalogSkuCount} SKUs
              {selectedRoom !== null
                ? <span className="text-foyr-deep normal-case tracking-normal">→ click to add to {selectedRoom.label}</span>
                : <span className="text-ink-500 normal-case tracking-normal">select a room above first</span>}
            </span>
            <span className="inline-flex items-center gap-1">
              {catalogCollapsed ? "show" : "hide"}
              <ArrowRight className={"w-3 h-3 transition " + (catalogCollapsed ? "-rotate-90" : "rotate-90")} />
            </span>
          </button>
          {!catalogCollapsed ? (
            <div className="px-2 pb-2 max-h-[420px] overflow-y-auto space-y-2">
              <ScanRoomCard
                graph={graph}
                projectId={activeId}
                selectedRoomId={selectedId}
                selectedRoomLabel={selectedRoom?.label ?? null}
                onMutated={() => setTick((t) => t + 1)}
              />
              <CatalogRail
                graph={graph}
                selectedRoomId={selectedId}
                onAdd={(sku, roomId, verdict) => {
                  const roomLabel = rooms.find((r) => r.id === roomId)?.label ?? roomId;
                  // Agentic mediation: every catalog click is a typed event.
                  // The agent ACCEPTS only when the spatial / clearance / affinity
                  // check passes; otherwise it REJECTS with the statutory or
                  // ontology citation. No silent disables — CLAUDE.md §5.
                  if (!verdict.ok) {
                    bus.emit({
                      agentId: "design-fit-check",
                      confidence: 1.0,
                      about: { kind: "Room", id: roomId },
                      wasInformedBy: [verdict.citation, "user/catalog-rail-rejected"],
                      proposed: {
                        kind: "create", nodeKind: "Suggestion", subjectId: `reject-${sku.id}-${roomId}-${Date.now()}`,
                        attrs: {
                          ruleId: "R-agent-rejects-placement",
                          ruleTitle: `Agent rejects: ${sku.label} in ${roomLabel}`,
                          severity: "block",
                          fix: verdict.reason,
                          citation: verdict.citation,
                          rejectedSkuId: sku.id,
                          rejectedSkuLabel: sku.label,
                          targetRoomId: roomId,
                        },
                        ts: new Date().toISOString(), origin: "agent",
                      },
                    });
                    // Timeline: the spatial fit-check agent (Neeraj Bhatia) rejected.
                    recordEvent(activeId, {
                      actorKind: "agent",
                      actorId: "spatial-fit-check",
                      verb: "rejected",
                      subjectKind: "SKU",
                      subjectId: sku.id,
                      subjectLabel: sku.label,
                      verdict: "block",
                      summary: `Cannot place ${sku.label} in ${roomLabel}.`,
                      citation: verdict.citation,
                      fix: verdict.reason,
                    });
                    // Visible toast so the user knows why the click didn't land.
                    setRejectionToast({ message: `${resolveActor("agent", "spatial-fit-check").displayName}: ${verdict.reason}`, citation: verdict.citation });
                    window.setTimeout(() => setRejectionToast(null), 6000);
                    return;
                  }
                  // Accepted — write the placement edge and emit either an
                  // info ("added") or warn ("unusual placement") suggestion.
                  addPlacement(activeId, sku.id, roomId);
                  setTick((t) => t + 1);
                  const isUnusual = verdict.soft !== undefined;
                  bus.emit({
                    agentId: "design-user-add",
                    confidence: 1.0,
                    about: { kind: "SKU", id: sku.id },
                    wasInformedBy: ["user/added-from-catalog", verdict.citation],
                    proposed: {
                      kind: "create", nodeKind: "Placement", subjectId: `user-place-${sku.id}-${roomId}`,
                      attrs: {
                        ruleId: isUnusual ? "R-unusual-placement" : "R-user-added-placement",
                        ruleTitle: isUnusual
                          ? `Unusual placement allowed: ${sku.label} in ${roomLabel}`
                          : "Added from catalog",
                        severity: isUnusual ? "warn" : "info",
                        fix: isUnusual
                          ? (verdict.soft ?? verdict.reason)
                          : `${sku.label} added to ${roomLabel}. ${verdict.reason}`,
                        citation: verdict.citation,
                      },
                      ts: new Date().toISOString(), origin: "user",
                    },
                  });
                  // Timeline: human added the SKU. Persona resolves from
                  // the signed-in user email so "Gaurav Malhotra" shows up,
                  // not "designer" generically.
                  const userId = (typeof window !== "undefined" && window.localStorage.getItem("foyrmind.userEmail")) || "designer";
                  recordEvent(activeId, {
                    actorKind: "human",
                    actorId: userId,
                    verb: "added",
                    subjectKind: "SKU",
                    subjectId: sku.id,
                    subjectLabel: sku.label,
                    verdict: isUnusual ? "warn" : "info",
                    summary: `${sku.label} placed in ${roomLabel}.`,
                    citation: verdict.citation,
                    ...(isUnusual && verdict.soft !== undefined ? { fix: verdict.soft } : {}),
                  });
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
    </>
  );
}

function sumPlacements(graph: ProjectGraph, rooms: ReadonlyArray<GraphNode>): number {
  return rooms.reduce((s, r) => s + skusInRoom(graph, r.id).length, 0);
}

/** Foyr-Neo-style vertical action rail anchored to the top-right of the
 *  3D stage. Lighting presets (Day/Evening/Night) + Top-down view toggle
 *  + a disabled "Render (Pro)" affordance that's honest about the E.0
 *  ComfyUI pipeline arriving later. */
function StageToolRail({
  lighting, onLighting, camera, onCamera, measureMode, onMeasure, labelsOn, onToggleLabels, onFlux, renderStatus, renderUnbound,
}: {
  lighting: "day" | "evening" | "night"; onLighting: (l: "day" | "evening" | "night") => void;
  camera: "perspective" | "topdown"; onCamera: (c: "perspective" | "topdown") => void;
  measureMode: boolean; onMeasure: () => void;
  labelsOn: boolean; onToggleLabels: () => void;
  onFlux: () => void;
  renderStatus: "idle" | "submitting" | "ok" | "error";
  renderUnbound: boolean;
}): JSX.Element {
  return (
    <div className="absolute top-3 right-3 flex flex-col gap-2 z-10">
      <div className="rounded-2xl border border-border/70 bg-cream/95 backdrop-blur-sm shadow-[0_2px_8px_rgba(0,0,0,0.08)] p-1.5 flex flex-col items-stretch">
        <div className="text-[8.5px] uppercase tracking-[0.12em] text-ink-500 px-1.5 pt-0.5 pb-1 text-center">Lighting</div>
        <RailButton icon={<Sun className="w-3.5 h-3.5" />}     label="Day"     active={lighting === "day"}     onClick={() => onLighting("day")} />
        <RailButton icon={<Sunset className="w-3.5 h-3.5" />}  label="Evening" active={lighting === "evening"} onClick={() => onLighting("evening")} />
        <RailButton icon={<Moon className="w-3.5 h-3.5" />}    label="Night"   active={lighting === "night"}   onClick={() => onLighting("night")} />
      </div>
      <div className="rounded-2xl border border-border/70 bg-cream/95 backdrop-blur-sm shadow-[0_2px_8px_rgba(0,0,0,0.08)] p-1.5 flex flex-col items-stretch">
        <div className="text-[8.5px] uppercase tracking-[0.12em] text-ink-500 px-1.5 pt-0.5 pb-1 text-center">View</div>
        <RailButton icon={<Box className="w-3.5 h-3.5" />}       label="Perspective" active={camera === "perspective"} onClick={() => onCamera("perspective")} />
        <RailButton icon={<ScanLine className="w-3.5 h-3.5" />}  label="Top-down"    active={camera === "topdown"}     onClick={() => onCamera("topdown")} />
      </div>
      <div className="rounded-2xl border border-border/70 bg-cream/95 backdrop-blur-sm shadow-[0_2px_8px_rgba(0,0,0,0.08)] p-1.5 flex flex-col items-stretch">
        <div className="text-[8.5px] uppercase tracking-[0.12em] text-ink-500 px-1.5 pt-0.5 pb-1 text-center">Measure</div>
        <RailButton icon={<Ruler className="w-3.5 h-3.5" />} label="Pick 2 pts" active={measureMode} onClick={onMeasure} />
      </div>
      <div className="rounded-2xl border border-border/70 bg-cream/95 backdrop-blur-sm shadow-[0_2px_8px_rgba(0,0,0,0.08)] p-1.5 flex flex-col items-stretch">
        <div className="text-[8.5px] uppercase tracking-[0.12em] text-ink-500 px-1.5 pt-0.5 pb-1 text-center">Labels</div>
        <RailButton icon={<Tag className="w-3.5 h-3.5" />} label={labelsOn ? "On" : "Off"} active={labelsOn} onClick={onToggleLabels} />
      </div>
      <div className="rounded-2xl border border-border/70 bg-cream/95 backdrop-blur-sm shadow-[0_2px_8px_rgba(0,0,0,0.08)] p-1.5 flex flex-col items-stretch">
        <div className="text-[8.5px] uppercase tracking-[0.12em] text-ink-500 px-1.5 pt-0.5 pb-1 text-center">Render</div>
        <button
          type="button"
          onClick={onFlux}
          disabled={renderStatus === "submitting" || renderUnbound}
          title={renderUnbound
            ? "ComfyUI tunnel unreachable. Will retry once comfy.hypergraphmind.com + the comfy-flux-dev APISIX route are live. Reload the page after the tunnel is bound."
            : "Submit to ComfyUI (Flux.1 dev) via APISIX X-LLM-Model: comfy-flux-dev."}
          className={
            "flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition " +
            (renderUnbound
              ? "bg-cream-dark text-ink-500 cursor-not-allowed opacity-60"
              : renderStatus === "submitting"
                ? "bg-ink-950 text-cream cursor-wait"
                : renderStatus === "error"
                  ? "bg-foyr-deep/10 text-foyr-deep border border-foyr-deep/40 hover:bg-foyr-deep/20"
                  : renderStatus === "ok"
                    ? "bg-fern/10 text-fern border border-fern/40"
                    : "text-ink-700 hover:bg-cream-dark/60 hover:text-ink-950")
          }
        >
          <Wand2 className={"w-3.5 h-3.5 " + (renderStatus === "submitting" ? "animate-pulse" : "")} />
          <span className="text-[9px] tracking-wide">{renderUnbound ? "Offline" : "Flux"}</span>
        </button>
      </div>
    </div>
  );
}

function RailButton({ icon, label, active, onClick }: { icon: JSX.Element; label: string; active: boolean; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg transition " +
        (active
          ? "bg-ink-950 text-cream shadow-[0_1px_2px_rgba(0,0,0,0.12)]"
          : "text-ink-700 hover:bg-cream-dark/60 hover:text-ink-950")
      }
      aria-pressed={active}
    >
      {icon}
      <span className="text-[9px] tracking-wide">{label}</span>
    </button>
  );
}

function ViewTab({ label, icon, active, onClick }: { label: string; icon: JSX.Element; active: boolean; onClick?: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border transition " +
        (active ? "bg-ink-950 text-cream border-ink-950" : "bg-cream border-border text-ink-500 hover:border-foyr/40 hover:text-ink-950")
      }
      aria-pressed={active}
    >
      {icon}{label}
    </button>
  );
}


/** Persistent per-room workspace card. Always visible above the stage
 *  when a room is selected — gives a stable "you're working on X" anchor
 *  with: room facts (kind/area/ceiling/min-dim), placed-SKU chips with
 *  one-click remove, cost + area utilization, and a count of active rule
 *  fires for this room.  The card is read-only data + remove action; the
 *  catalog rail below is the *add* path; the stage shows geometry. */
function RoomWorkspaceCard({ graph, room, score, onRemove, activeProjectId }: {
  graph: ProjectGraph;
  room: GraphNode;
  score: RoomScore | undefined;
  onRemove: (skuId: string, roomId: string) => void;
  activeProjectId: string;
}): JSX.Element {
  void activeProjectId;
  const skus = skusInRoom(graph, room.id);
  const roomKind = attrStr(room, "roomKind");
  const sqFt = attrNum(room, "area");
  const sqM = attrNum(room, "areaSqM") ?? (sqFt !== null ? sqFt * 0.092903 : null);
  const ceilingFt = attrNum(room, "ceilingHt");
  const minDimM = attrNum(room, "minDimM");
  const skuValue = skus.reduce((s, x) => s + (attrNum(x, "price") ?? 0), 0);
  // Rough utilization: sum of category footprints / room area.
  const utilSqM = (() => {
    if (sqM === null || sqM <= 0) return null;
    let consumed = 0;
    for (const s of skus) {
      const cat = attrStr(s, "category");
      // Use a default heuristic when category absent (avg furniture ~1.5 m²).
      consumed += cat !== null ? 1.8 : 1.2;
    }
    return Math.min(1, consumed / sqM);
  })();
  const utilPct = utilSqM !== null ? Math.round(utilSqM * 100) : null;
  const utilTone = utilPct === null ? "miss" : utilPct < 35 ? "miss" : utilPct < 75 ? "ok" : "warn";

  return (
    <section className="rounded-2xl border border-border/70 bg-cream px-3 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
      <div className="flex items-center gap-3 flex-wrap">
        {/* Room label + kind — compact left block. */}
        <div className="flex items-baseline gap-2 min-w-0">
          <Sparkles className="w-3 h-3 text-foyr-deep shrink-0" />
          <span className="text-[10px] uppercase tracking-[0.14em] text-ink-500">Working on</span>
          <span className="font-newyork text-[18px] text-ink-950 leading-none truncate">{room.label}</span>
          {roomKind !== null ? <span className="text-[10.5px] text-ink-500 tracking-normal hidden sm:inline">· {roomKind}</span> : null}
        </div>
        {/* Compact facts — every chip is a single readable token. */}
        <div className="flex flex-wrap items-center gap-1 text-[10.5px] tabular">
          {sqFt !== null ? <span className="pill border border-border bg-cream-dark/30 text-ink-700">{sqFt} sq ft{sqM !== null ? ` · ${sqM.toFixed(1)} m²` : ""}</span> : null}
          {ceilingFt !== null ? <span className="pill border border-border bg-cream-dark/30 text-ink-700">{ceilingFt}′</span> : null}
          {minDimM !== null ? <span className="pill border border-border bg-cream-dark/30 text-ink-700">min {minDimM.toFixed(2)}m</span> : null}
          {(score?.rulesFired ?? 0) > 0
            ? <span className="pill border border-foyr/40 bg-foyr/10 text-foyr-deep inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{score?.rulesFired}</span>
            : <span className="pill border border-fern/40 bg-fern/10 text-fern inline-flex items-center gap-1"><CircleCheck className="w-3 h-3" />clean</span>}
        </div>
        {/* Inline metrics on the right — no separate column wasting space. */}
        <div className="flex items-center gap-2 text-[11px] tabular ml-auto">
          <span className="inline-flex items-center gap-0.5 text-ink-700 font-medium"><IndianRupee className="w-3 h-3 text-foyr" />{formatMoney(skuValue)}</span>
          {utilPct !== null ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-16 h-1 rounded-full bg-cream-dark overflow-hidden">
                <span
                  className={"block h-full rounded-full " + (utilTone === "warn" ? "bg-foyr" : utilTone === "ok" ? "bg-fern" : "bg-ink-950/30")}
                  style={{ width: `${Math.min(100, utilPct)}%` }}
                />
              </span>
              <span className="text-ink-500 text-[10px]">{utilPct}%</span>
            </span>
          ) : null}
        </div>
      </div>
      {/* Placements row — only renders when present. Wraps gracefully. */}
      {skus.length > 0 ? (
        <ul className="flex flex-wrap gap-1 mt-1.5 pt-1.5 border-t border-border/40">
          {skus.map((s) => {
            const userAdded = isUserAdded(activeProjectId, s.id, room.id);
            const price = attrNum(s, "price");
            const photo = photoForSkuNode(s);
            return (
              <li key={s.id}>
                <span className="inline-flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded-md border border-border bg-cream-dark/30 text-[10.5px] text-ink-950">
                  <img src={photo} alt={s.label} className="w-4 h-4 rounded object-cover bg-cream" loading="lazy" />
                  <span className="truncate max-w-[120px]" title={s.label}>{s.label}</span>
                  {price !== null ? <span className="text-ink-500 tabular text-[9.5px]">{formatCompactMoney(price)}</span> : null}
                  {userAdded ? (
                    <button
                      type="button"
                      onClick={() => onRemove(s.id, room.id)}
                      className="grid place-items-center w-3.5 h-3.5 rounded-full hover:bg-foyr/20 text-ink-500 hover:text-foyr-deep transition"
                      title="Remove"
                      aria-label={`Remove ${s.label}`}
                    >
                      <XIcon className="w-2.5 h-2.5" />
                    </button>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

function RoomStage({ graph, room, score, onRemove, activeProjectId, onSkuMoved, onSkuRotated, onCatalogDrop }: {
  graph: ProjectGraph; room: GraphNode; score: RoomScore | undefined;
  onRemove: (skuId: string, roomId: string) => void;
  activeProjectId: string;
  onSkuMoved: (skuId: string, roomId: string, xRelM: number, zRelM: number) => void;
  onSkuRotated?: (skuId: string, roomId: string, nextYaw: number) => void;
  /** Drop target — catalog tile dropped onto the plan. The relative
   *  metres are computed from the cursor's SVG position against the
   *  PlanMetrics so the SKU lands where the designer released. */
  onCatalogDrop?: (skuId: string, roomId: string, xRelM: number, zRelM: number) => void;
}): JSX.Element {
  const skus = skusInRoom(graph, room.id);
  // Typed Material nodes from the live graph — drive the finish dropdown
  // on each placement row (A.5). When empty, the dropdown is hidden.
  const materials = graph.nodes.filter((n) => n.kind === "Material");
  // SVG paints the room shell + doors + dimensions. The interactive
  // SKU markers live in the React overlay below so 2D + 3D share the
  // same typed posX_${roomId} / posZ_${roomId} override — drop on one
  // surface, see the move on the other automatically.
  const svg = renderFloorPlanSvg({ graph, roomId: room.id, width: 880, height: 540, skipSkuIcons: true });
  const planMetrics = getPlanMetrics({ graph, roomId: room.id });
  const roomKind = attrStr(room, "roomKind");
  const sqFt = attrNum(room, "area");
  const ceilingFt = attrNum(room, "ceilingHt");
  const skuValue = skus.reduce((s, x) => s + (attrNum(x, "price") ?? 0), 0);

  return (
    <section className="panel p-4 space-y-3">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-[20px] font-medium text-ink-950 leading-tight">{room.label}</h2>
          <div className="text-[11px] text-ink-500 tabular mt-0.5">
            {room.id}{roomKind ? ` · ${roomKind}` : ""}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 text-[10.5px] text-ink-500 tabular">
          {sqFt !== null ? <span className="pill bg-cream-dark border border-border">{sqFt} sq ft</span> : null}
          {ceilingFt !== null ? <span className="pill bg-cream-dark border border-border">{ceilingFt} ft H</span> : null}
          <span className="pill bg-cream-dark border border-border">{skus.length} placements</span>
          <span className="pill bg-cream-dark border border-border">{formatCompactMoney(skuValue)}</span>
          {(score?.rulesFired ?? 0) > 0 ? (
            <span className="pill bg-foyr/10 border border-foyr/30 text-foyr-deep inline-flex items-center gap-1">
              <AlertTriangle className="w-2.5 h-2.5" /> {score?.rulesFired} rule{score?.rulesFired === 1 ? "" : "s"}
            </span>
          ) : (
            <span className="pill bg-fern/10 border border-fern/30 text-fern inline-flex items-center gap-1">
              <CircleCheck className="w-2.5 h-2.5" /> rules clean
            </span>
          )}
        </div>
      </header>

      {svg.length > 0 ? (
        <div
          className="relative rounded-xl border border-border/70 overflow-hidden bg-white"
          onDragOver={(e) => {
            if (!onCatalogDrop || !planMetrics) return;
            const hasSku = e.dataTransfer.types.includes("application/x-foyr-sku");
            if (!hasSku) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }}
          onDrop={(e) => {
            if (!onCatalogDrop || !planMetrics) return;
            const skuId = e.dataTransfer.getData("application/x-foyr-sku");
            if (!skuId) return;
            e.preventDefault();
            // Find the FloorPlanOverlay's SVG to convert client coords
            // → SVG viewBox coords → room-relative metres. Single source
            // of truth via getScreenCTM().inverse() (same path as the
            // overlay's own drag).
            const svgEl = (e.currentTarget as HTMLDivElement).querySelector("svg") as SVGSVGElement | null;
            if (!svgEl) return;
            const pt = svgEl.createSVGPoint();
            pt.x = e.clientX;
            pt.y = e.clientY;
            const ctm = svgEl.getScreenCTM();
            if (!ctm) return;
            const local = pt.matrixTransform(ctm.inverse());
            const xRelPx = local.x - planMetrics.originPx.x;
            const zRelPx = local.y - planMetrics.originPx.y;
            const xRelM = xRelPx / planMetrics.pxPerFt / planMetrics.ftPerM;
            const zRelM = zRelPx / planMetrics.pxPerFt / planMetrics.ftPerM;
            onCatalogDrop(skuId, room.id, xRelM, zRelM);
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: svg }} />
          {planMetrics ? (
            <FloorPlanOverlay
              graph={graph}
              roomId={room.id}
              metrics={planMetrics}
              onSkuMoved={onSkuMoved}
              {...(onSkuRotated ? { onSkuRotated } : {})}
            />
          ) : null}
          {/* A.1 tldraw scaffold — read-only preview overlaid when the
              VITE_FLOOR_PLAN_TLDRAW flag is "true". Off by default; the
              SVG path above stays canonical until tldraw reaches
              feature parity. */}
          {env.VITE_FLOOR_PLAN_TLDRAW === "true" && planMetrics ? (
            <div className="absolute inset-0 pointer-events-none opacity-50">
              <FloorPlan2DTldraw graph={graph} roomId={room.id} metrics={planMetrics} />
            </div>
          ) : null}
          <div className="absolute top-2 left-2 pill bg-cream/90 border border-border text-ink-500 text-[9.5px] uppercase tracking-wider backdrop-blur-sm">
            click to select · drag to move · drag from catalog to drop · ↻ or R to rotate
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-border/70 bg-cream-dark/50 p-6 text-[12.5px] text-ink-500">
          No FloorPlan node attached to this Room yet. Add one via the Drawings tab to enable agentic geometric checks.
        </div>
      )}

      <ScoreBar score={score} />

      <div>
        <div className="text-[11px] uppercase tracking-wider text-ink-500 mb-1.5 flex items-center gap-1.5">
          <Tag className="w-3 h-3" /> Placements · {skus.length}
        </div>
        {skus.length === 0 ? (
          <div className="text-[12.5px] text-ink-500 italic">
            No SKUs in this room — see the agent rail for what to add first.
          </div>
        ) : (
          <ul className="space-y-1">
            {skus.map((sku) => {
              const price = attrNum(sku, "price");
              const lead = attrNum(sku, "leadDays");
              const style = attrNum(sku, "styleMatch");
              const tone = (style ?? 0) >= 0.85 ? "text-fern" : (style ?? 0) >= 0.7 ? "text-foyr-deep" : "text-ink-500";
              const photoUrl = photoForSkuNode(sku);
              const userAdded = isUserAdded(activeProjectId, sku.id, room.id);
              const finishMaterialId = (sku.attrs[`finishMaterialId_${room.id}`] as string | undefined) ?? "";
              return (
                <li key={sku.id} className="flex items-center justify-between gap-3 text-[12.5px]">
                  <span className="flex items-center gap-2 min-w-0">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={sku.label}
                        className="w-9 h-9 rounded-md object-cover border border-border/70 shrink-0"
                        loading="lazy"
                      />
                    ) : null}
                    <span className="text-ink-950 truncate">{sku.label}</span>
                    {userAdded ? (
                      <span className="pill text-[9px] bg-foyr/10 border border-foyr/30 text-foyr-deep shrink-0">added</span>
                    ) : null}
                    {/* Finish picker — typed Material nodes from the live
                        project graph. Sticky per (sku, room). Empty = no
                        finish; agentSuggestions flags it as "specify finish". */}
                    {materials.length > 0 ? (
                      <select
                        value={finishMaterialId}
                        onChange={(e) => {
                          const next = e.target.value;
                          if (next) {
                            import("../lib/userFinishes").then((m) => {
                              m.setFinish(activeProjectId, sku.id, room.id, next);
                              window.dispatchEvent(new Event("foyrmind:timeline"));
                            });
                          } else {
                            import("../lib/userFinishes").then((m) => {
                              m.clearFinish(activeProjectId, sku.id, room.id);
                              window.dispatchEvent(new Event("foyrmind:timeline"));
                            });
                          }
                        }}
                        className="text-[10px] bg-cream-dark border border-border rounded-md px-1.5 py-0.5 text-ink-700 focus:outline-none focus:border-foyr/60"
                        title="Apply a typed Material as the finish for this placement"
                      >
                        <option value="">no finish</option>
                        {materials.map((mat) => (
                          <option key={mat.id} value={mat.id}>{mat.label}</option>
                        ))}
                      </select>
                    ) : null}
                  </span>
                  <span className="flex items-center gap-2 shrink-0 text-[11px] text-ink-500 tabular">
                    {lead !== null ? <span className={lead >= 60 ? "text-foyr-deep" : ""}>{lead}d</span> : null}
                    {style !== null ? <span className={tone}>{Math.round(style * 100)}%</span> : null}
                    {price !== null ? <span className="font-medium text-ink-950">{formatMoney(price)}</span> : null}
                    {userAdded ? (
                      <button
                        type="button"
                        onClick={() => onRemove(sku.id, room.id)}
                        className="btn btn-ghost p-1 text-ink-500 hover:text-foyr-deep"
                        aria-label={`Remove ${sku.label} from ${room.label}`}
                        title="Remove from this room"
                      >
                        <XIcon className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <ArrowRight className="w-3 h-3" />
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

function ScoreBar({ score }: { score: RoomScore | undefined }): JSX.Element | null {
  if (!score) return null;
  return (
    <div className="grid grid-cols-3 gap-2 text-[10.5px]">
      <ScoreCell
        icon={<Sparkles className="w-3 h-3" />}
        label="Style cohesion"
        value={`${Math.round(score.style * 100)}%`}
        good={score.style >= 0.85}
      />
      <ScoreCell
        icon={<IndianRupee className="w-3 h-3" />}
        label="Budget pull"
        value={`${Math.round(score.budgetUtil * 100)}%`}
        good={score.budgetUtil < 0.9}
      />
      <ScoreCell
        icon={<AlertTriangle className="w-3 h-3" />}
        label="Max lead"
        value={score.leadRiskMax > 0 ? `${score.leadRiskMax} d` : "—"}
        good={score.leadRiskMax > 0 && score.leadRiskMax < 60}
      />
    </div>
  );
}

function ScoreCell({ icon, label, value, good }: { icon: JSX.Element; label: string; value: string; good: boolean }): JSX.Element {
  return (
    <div className={"rounded-lg border px-2.5 py-2 flex items-center gap-2 " + (good ? "bg-fern/5 border-fern/30" : "bg-foyr/5 border-foyr/30")}>
      <span className={good ? "text-fern" : "text-foyr-deep"}>{icon}</span>
      <div className="min-w-0">
        <div className={"text-[9.5px] uppercase tracking-wider " + (good ? "text-fern" : "text-foyr-deep")}>{label}</div>
        <div className="text-[12px] font-medium text-ink-950 tabular">{value}</div>
      </div>
    </div>
  );
}

function AgentRail({ bus }: { bus: AgentBus }): JSX.Element {
  return (
    <aside className="panel p-3 space-y-2 sticky top-[80px]">
      <div className="flex items-center justify-between">
        <div className="eyebrow inline-flex items-center gap-1.5 text-foyr-deep">
          <Lightbulb className="w-3 h-3" /> Agent rail
        </div>
        <div className="text-[10px] text-ink-500 tabular inline-flex items-center gap-2">
          <kbd className="px-1.5 py-0.5 rounded bg-cream-dark border border-border">↹</kbd>
          accept
          <kbd className="px-1.5 py-0.5 rounded bg-cream-dark border border-border">␛</kbd>
          reject
        </div>
      </div>
      <SuggestionStrip bus={bus} />
      <div className="text-[10.5px] text-ink-500 leading-snug">
        The agent walks the project's promoted rules + room-anchor heuristics on every project switch.
        Each suggestion is a typed <code className="font-mono">prov:Activity</code> with a Why chain — accept and it becomes a real Placement; reject and the system learns.
      </div>
    </aside>
  );
}
